import { observer } from "mobx-react-lite";
import { useState } from "react";

import { usePromptOperations } from "./hooks/usePromptOperations";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Messages from "@/components/Playground/Messages";
import CommitMessageDialog from "@/components/ui/CommitMessageDialog";
import { usePlayground } from "@/components/Playground/playground-store";
import { useAuth } from "@/context/auth-context";

const SaveButtonText = ({ isSaving, mode }: { isSaving: boolean; mode: "create" | "edit" }) => {
  if (isSaving) {
    return <>Saving...</>;
  }

  if (mode === "create") {
    return <>Create Prompt</>;
  }

  return <>Save New Version</>;
};

interface PromptEditorContentProps {
  mode: "create" | "edit";
  workspaceId?: number;
  existingPromptId?: number;
  existingPromptName?: string;
  existingVersionNumber?: number;
  existingLabel?: string;
  onSaveComplete: (result: { id: number; name: string; version?: number }) => void;
  onClose: () => void;
}

const PromptEditorContent = observer(
  ({
    mode,
    workspaceId,
    existingPromptId,
    existingPromptName,

    onSaveComplete,
    onClose,
  }: PromptEditorContentProps) => {
    const auth = useAuth();
    const userToken = auth?.userToken || "";
    const [promptName, setPromptName] = useState("");
    const playground = usePlayground();

    // Guard against missing workspace
    if (!workspaceId) {
      return (
        <div className="flex h-full items-center justify-center">
          <div className="text-gray-500">Loading workspace...</div>
        </div>
      );
    }

    // Handle save operations
    const {
      isSaving,
      submittedVersion,
      showCommitDialog,
      setShowCommitDialog,
      handleSaveEdit,
      handleSave,
    } = usePromptOperations({
      userToken,
      workspaceId,
      mode,
      existingPromptId,
      existingPromptName,
      parentVersionId: playground.selectedTemplateVersion?.id,
      onSaveComplete,
      onClose,
    });

    const handleSaveClick = () => {
      handleSave(promptName, playground.blueprint);
    };

    const handleCommitConfirm = (commitMessage?: string) => {
      handleSaveEdit(commitMessage, playground.blueprint);
    };

    return (
      <>
        <div className="flex h-full flex-col">
          {mode === "create" && (
            <div
              className="flex items-center gap-3 border-b"
              style={{
                paddingLeft: "24px",
                paddingRight: "24px",
                paddingTop: "16px",
                paddingBottom: "16px",
              }}
            >
              <span className="text-sm font-medium text-gray-700">Prompt Name:</span>
              <Input
                placeholder="Enter prompt name..."
                value={promptName}
                onChange={e => setPromptName(e.target.value)}
                className="max-w-sm"
              />
            </div>
          )}

          <div
            className="flex-1 overflow-hidden"
            style={{
              paddingLeft: "24px",
              paddingRight: "24px",
              paddingTop: "16px",
              paddingBottom: "16px",
            }}
          >
            <Messages
              messages={playground.messages}
              onAddMessage={() => {
                playground.addEmptyMessage();
              }}
              title={mode === "edit" ? `Edit Prompt: ${existingPromptName}` : "Create New Prompt"}
            />
          </div>

          <div
            className="flex justify-end gap-3 border-t"
            style={{
              paddingLeft: "32px",
              paddingRight: "32px",
              paddingTop: "24px",
              paddingBottom: "24px",
            }}
          >
            <Button variant="outline" onClick={onClose} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveClick}
              disabled={isSaving || (mode === "create" && !promptName)}
            >
              <SaveButtonText isSaving={isSaving} mode={mode} />
            </Button>
          </div>
        </div>

        {/* Commit Message Dialog for Edit Mode */}
        <CommitMessageDialog
          isOpen={showCommitDialog}
          onClose={() => {
            setShowCommitDialog(false);
            if (submittedVersion) {
              onSaveComplete({
                id: submittedVersion.prompt_id,
                name: existingPromptName || "",
                version: submittedVersion.number,
              });
            }
          }}
          onConfirm={handleCommitConfirm}
          data={playground.blueprint}
          prompt={{
            name: existingPromptName,
            id: existingPromptId,
          }}
          isLoading={isSaving}
          submittedVersion={submittedVersion}
          disableRedirect
        />
      </>
    );
  },
);

export default PromptEditorContent;
